import type { FacebookQuery, Setup } from './types';

export * from './types';

/**
 * Some environments may pass boolean values as strings. This function
 * will parse the value and return a boolean.
 *
 * @param value - The value to parse
 * @returns - The boolean interpretation of the value
 */
const parseStringOrBoolean = (value: string | boolean | undefined) => {
    if (typeof value === "string") {
        return value === "true";
    }

    return value;
};

export function addScript(
    f: Window,
    b: Document,
    e: string,
    v: string,
    n?: any,
    t?: HTMLScriptElement,
    s?: HTMLScriptElement): FacebookQuery
{
    if (f.fbq) { return f.fbq };

    const runtimeConfig = useRuntimeConfig();
    const isDebug = parseStringOrBoolean(runtimeConfig.public.metapixel.debug);
    const isEnabled = parseStringOrBoolean(runtimeConfig.public.metapixel.enabled);

    n = f.fbq = (isEnabled
        ? function () {
            const cmd = arguments[0].toLowerCase();

            //console.log('[Meta-Pixel]', 'Consent:', n.consent, 'Command:', cmd, 'Initialized:', n.initialized, 'HasQueuedInitial', n.hasQueuedInitial);
            if (n.consent || cmd === 'consent' || !n.initialized || !n.hasQueuedInitial) {
                if (isDebug) {
                    console.log('[Meta-Pixel] Event', `(${Array.from(arguments).join(', ')})`);
                }

                // We set the consent value.
                if (cmd === 'consent') {
                    n.consent = arguments[1] === 'grant';
                }

                if (n.callMethod) {
                    // eslint-disable-next-line prefer-spread, prefer-rest-params
                    n.callMethod.apply(n, arguments);
                } else {
                    // eslint-disable-next-line prefer-rest-params
                    n.queue.push(arguments);
                }
            } else if (!n.consent) {
                if (isDebug) {
                    console.log('[Meta-Pixel] Event: Not tracked. No active consent.', `(${Array.from(arguments).join(', ')})`);
                }
            }
        } : function () {
            if (isDebug) {
                console.log('[Meta-Pixel] Event supressed:', `(${Array.from(arguments).join(', ')})`);
            }
        }) as unknown as FacebookQuery;

    if (!f._fbq) { f._fbq = n };

    // The module id enabled. We do not load the Meta Pixel script.
    if (isEnabled) {
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        n.disablePushState = true;
        n.consent = false;
        n.initialized = false;
        n.hasQueuedInitial = false;

        t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;

        s = b.getElementsByTagName(e)[0] as HTMLScriptElement;
        s.parentNode!.insertBefore(t, s);
    }

    return n;
}

export function addScriptDefault() {
    return addScript(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
}

export function setup($fbq: FacebookQuery = addScriptDefault()): Setup {
    function init(
        pixelId: string,
        autoconfig?: boolean,
        consent?: string) {
        $fbq('consent', consent ?? 'revoke');
        $fbq('set', 'autoConfig', autoconfig ?? false, pixelId);
        $fbq('init', pixelId);

        $fbq.initialized = true;

        return setup($fbq); 
    }

    function pageView(pixelId?: string) {
        if (pixelId === undefined) {
            $fbq('track', 'PageView');
        } else {
            $fbq('trackSingle', pixelId, 'PageView');
        };

        $fbq.hasQueuedInitial = true;

        return setup($fbq);
    }

    return {
        $fbq,
        init,
        pageView
    }
}